/**
 * Shared helpers for outsourced user FTE domain models.
 * Used by both FteTrackingItem (FTE Tracking admin) and OutsourcedUserFteItem (User admin).
 */

import dateTimeHelper from '../../Helpers/datetimeHelper';

export function isEntryLocked(item) {
  // Cancelled counts as locked here: the row is a terminal audit record, so edit/lock/delete
  // must stay disabled for it just as they are for Locked/Invoiced rows.
  return item.Status === 'Locked' || item.Status === 'Invoiced' || item.Status === 'Cancelled';
}

export function canEntryBeCancelled(item) {
  // Mirrors the backend rule: only a Locked row that was never invoiced can be cancelled.
  // An invoiced row is unwound by reversing its IOB instead.
  return item.Status === 'Locked' && !item.IoblineItemId;
}

export function hasLiveBonusShare(item) {
  // Mirrors the backend rule for a bonus-only reversal: the row still carries a bonus share that has not been
  // reversed. A reversed row never qualifies — reversing it already reversed its bonus.
  if (item.ReversedOnUtc) return false;
  return (item.BonusIobs || []).some((bonus) => !bonus.reversedOnUtc);
}

/**
 * The salary reverse IOB and every reverse IOB that carried the row's bonus shares, for the grid's Reverse IOB
 * column. A row reversed in one go has both on the same invoice, so the ids are de-duplicated.
 */
export function getReverseIobInvoiceIds(item) {
  const ids = [item.ReverseIobInvoiceId, ...(item.BonusIobs || []).map((bonus) => bonus.reverseIobInvoiceId)];
  return [...new Set(ids.filter((id) => !!id))];
}

/**
 * The hint under the FTE Split Rate (%) input. The value is entered as a percentage (100 = full time), but the
 * FTE Tracking grid shows it as a fraction (100 -> 1), so someone typing 1 for a full-time person stores 1%
 * and sees 0.01 in the grid. Echoing the fraction back while typing makes that visible before saving.
 */
export function describeFtePercentage(value) {
  const percentage = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(percentage)) {
    return 'Enter a percentage: 100 is full time.';
  }
  return `${percentage}% = ${Number((percentage / 100).toFixed(4))} FTE (100 is full time).`;
}

/**
 * Normalizes a date-picker value to a plain calendar-day string (yyyy-MM-dd).
 * The picker emits a Date object; JSON.stringify would turn that into a UTC
 * instant of local midnight, which a non-local (e.g. UTC) server then parses as
 * the previous day. Sending the local calendar day as a bare string makes the
 * value timezone-independent. Strings (already yyyy-MM-dd from the API) pass through.
 */
export function toDateOnlyString(value) {
  if (!value) return value;
  if (typeof value === 'string') return value.slice(0, 10);
  return dateTimeHelper.toYYYYsMMsDD(value, '-');
}

export function mapBaseFields(target, item) {
  target.OutsourcedUserFteid = item.outsourcedUserFteid;
  target.UserId = item.userId;
  target.FieldworkOfficeId = item.fieldworkOfficeId;
  target.FieldworkOfficeName = item.fieldworkOfficeName;
  target.HiringOfficeId = item.hiringOfficeId;
  target.HiringOfficeName = item.hiringOfficeName;
  target.Ftepercentage = item.ftepercentage;
  target.StartDateUtc = item.startDateUtc;
  target.EndDateUtc = item.endDateUtc;
  target.Status = item.status;
  target.CancelledOnUtc = item.cancelledOnUtc;
  target.CancelledByName = item.cancelledByName;
  target.ReversedOnUtc = item.reversedOnUtc;
  target.ReversedByName = item.reversedByName;
  target.ModifiedDesc = item.modifiedDesc;
}

/**
 * Tooltip text for the Cancelled/Reversed grid badges: "<verb> on 2026-08-05 by Jane Doe",
 * degrading gracefully when the date or name is missing.
 */
export function formatAuditTooltip(verb, dateValue, byName) {
  const parts = [verb];
  if (dateValue) parts.push(`on ${String(dateValue).slice(0, 10)}`);
  if (byName) parts.push(`by ${byName}`);
  return parts.join(' ');
}

export function initBaseFields(target) {
  target.OutsourcedUserFteid = '';
  target.UserId = '';
  target.FieldworkOfficeId = '';
  target.FieldworkOfficeName = '';
  target.HiringOfficeId = '';
  target.HiringOfficeName = '';
  target.Ftepercentage = '';
  target.StartDateUtc = '';
  target.EndDateUtc = '';
  target.Status = '';
  target.CancelledOnUtc = null;
  target.CancelledByName = null;
  target.ReversedOnUtc = null;
  target.ReversedByName = null;
  target.ModifiedDesc = '';
}
